// The ONE plural rule. A count-bearing string is stored as a FAMILY of two keys,
// `<key>_one` and `<key>_other`, and picked by the locale's CLDR category, never by
// a hand-written `count === 1 ? t(a) : t(b)` at the call site: those drifted into
// two key conventions (`key`/`key_plural` and `key_one`/`key`) and shipped
// "1 new lines".
import type { Catalog } from './catalog';

/** The two keys a plural family `key` is stored under, `_one` first. */
export function pluralKeys(key: string): readonly [string, string] {
  return [`${key}_one`, `${key}_other`];
}

/**
 * The variant key `count` selects under `rules`: CLDR `one` -> `_one`, every
 * other category (`zero`, `two`, `few`, `many`, `other`) -> `_other`. A locale
 * with no `one` category (zh) therefore takes `_other` for 1, which is correct.
 */
export function pluralCategoryKey(rules: Intl.PluralRules, key: string, count: number): string {
  const [one, other] = pluralKeys(key);
  return rules.select(count) === 'one' ? one : other;
}

/**
 * Whether `catalog` resolves `key` as a translation: its own entry, or a plural
 * family carrying BOTH variants. The verdict a key-coverage guard reads, so a
 * `t.plural` key is neither "missing" (no bare entry) nor half a family.
 */
export function resolvesKey(catalog: Catalog, key: string): boolean {
  return Object.hasOwn(catalog, key) || pluralKeys(key).every((k) => Object.hasOwn(catalog, k));
}
